/**
 * Falha de requisição explicada para a pessoa, e a política de repetir ou parar.
 *
 * Duas decisões que moravam espalhadas e que dão o mesmo problema quando erram:
 * - O QUE DIZER: "Não foi possível carregar" não diz se o problema é a conexão, o servidor ou o acesso. Cada causa tem um
 *   texto próprio em pt-BR e diz o que fazer (esperar, falar com o administrador, regularizar o pagamento).
 * - QUANDO REPETIR: erro 4xx (sem licença, sem permissão, plano sem o recurso) não muda por tentar de novo. Repetir e
 *   continuar consultando a cada poucos segundos só enche o console e o servidor de pedidos recusados. Só vale repetir
 *   quando a falha pode passar sozinha (conexão, 5xx, 408, e 429 quando a pessoa pede).
 *
 * Aceita os dois formatos de erro do app: `ApiError` (`status`, `data.code`) e `VideoCallApiError` (`status`, `code`).
 */

/** Textos genéricos que a camada de rede usa quando não tem nada melhor: não adianta repassá-los à pessoa. */
const GENERIC_MESSAGES = new Set(['Erro na requisição', 'Erro interno do servidor', 'Failed to fetch', 'Load failed', 'NetworkError']);

/** `0` quando não há status (sem conexão, requisição cancelada, erro de código). */
export function failureStatus(error) {
    const status = Number(error?.status);
    return Number.isFinite(status) ? status : 0;
}

export const failureCode = (error) => error?.code ?? error?.data?.code ?? null;

/**
 * Erro do cliente que repetir não resolve (4xx, menos 408 que é tempo esgotado e pode passar).
 * @param {unknown} error
 */
export function isClientError(error) {
    const status = failureStatus(error);
    return status >= 400 && status < 500 && status !== 408;
}

/**
 * `retry` do React Query: nunca repete erro do cliente; o resto tenta até duas vezes. Vale como padrão do app.
 * @param {number} failureCount
 * @param {unknown} error
 */
export function shouldRetryRequest(failureCount, error) {
    if (isClientError(error)) return false;
    return failureCount < 2;
}

/**
 * `refetchInterval` do React Query que PARA quando a consulta falhou por erro do cliente: sem licença ou sem permissão a
 * lista não vai passar a funcionar sozinha, e a pessoa pode usar "Tentar de novo" (ou o dado volta a ser lido ao voltar).
 * @param {number} intervalMs
 * @returns {(query: { state: { error: unknown } }) => number | false}
 */
export const pollUnlessClientError = (intervalMs) => (query) => (isClientError(query?.state?.error) ? false : intervalMs);

/**
 * @typedef {object} ApiFailure
 * @property {'license'|'plan'|'permission'|'seat'|'billing'|'rate'|'server'|'network'|'rejected'} kind
 * @property {string} title curto, vira o título do aviso
 * @property {string} message o que aconteceu e o que fazer
 * @property {boolean} retryable tentar de novo pode resolver sozinho (conexão, servidor, excesso de pedidos)
 */

/** O texto do servidor serve à pessoa? (escrito para ela, em pt-BR, e não um texto da camada de rede) */
function usableServerMessage(error) {
    const message = typeof error?.message === 'string' ? error.message.trim() : '';
    return message && !GENERIC_MESSAGES.has(message) ? message : null;
}

const BY_CODE = {
    OTTO_LICENSE_REQUIRED: {
        kind: 'license',
        title: 'Você não tem uma licença do Otto',
        message: 'Peça ao administrador da conta para atribuir uma licença a você.',
    },
    OTTO_LICENSE_LIMIT_EXCEEDED: {
        kind: 'license',
        title: 'Sua licença está acima do limite do plano',
        message: 'O plano da conta inclui menos licenças do que as atribuídas. Peça ao administrador para retirar outra licença ou fazer upgrade do plano.',
    },
    FEATURE_BLOCKED: {
        kind: 'plan',
        title: 'Recurso fora do seu plano',
        message: 'O plano da conta não inclui este recurso. Fale com o administrador para mudar de plano.',
    },
    PERMISSION_DENIED: {
        kind: 'permission',
        title: 'Sem permissão de acesso',
        message: 'Seu perfil não tem permissão para ver isto. Peça ao administrador da conta para liberar o acesso.',
    },
    SEAT_BLOCKED: {
        kind: 'seat',
        title: 'Conta acima do limite de usuários',
        message: 'A conta tem mais usuários do que o plano permite. Fale com o administrador para ajustar o plano.',
    },
};

/**
 * Explica uma falha para a pessoa. Nunca devolve o texto cru de erro de código nem de rede.
 *
 * @param {unknown} error
 * @returns {ApiFailure}
 */
export function describeApiFailure(error) {
    const known = BY_CODE[failureCode(error)];
    if (known) return { ...known, retryable: false };

    const status = failureStatus(error);

    if (status === 402) {
        return {
            kind: 'billing',
            title: 'Assinatura com pendência',
            message: 'Há uma pendência de pagamento na conta. Regularize para voltar a usar.',
            retryable: false,
        };
    }
    if (status === 403) {
        return {
            kind: 'permission',
            title: 'Acesso não permitido',
            message: usableServerMessage(error) ?? 'Você não tem acesso a isto. Peça ao administrador da conta para liberar.',
            retryable: false,
        };
    }
    if (status === 429) {
        return {
            kind: 'rate',
            title: 'Muitas tentativas seguidas',
            message: 'Aguarde alguns instantes e tente de novo.',
            retryable: true,
        };
    }
    if (status >= 500) {
        return {
            kind: 'server',
            title: 'Algo deu errado do nosso lado',
            message: 'Já registramos o problema. Tente de novo em instantes.',
            retryable: true,
        };
    }
    if (status >= 400) {
        return {
            kind: 'rejected',
            title: 'Não foi possível concluir',
            message: usableServerMessage(error) ?? 'O pedido não foi aceito. Atualize a página e tente de novo.',
            retryable: false,
        };
    }
    return {
        kind: 'network',
        title: 'Sem conexão com o servidor',
        message: 'Verifique sua internet e tente de novo.',
        retryable: true,
    };
}
